import { useState } from 'react';
import { Link } from 'react-router';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Modal, RadioButton, RadioButtonGroup, Select, SelectItem, Tag, TextInput } from '@carbon/react';
import type { ColumnDef } from '@tanstack/react-table';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { InlineError } from './states';
import { DataTable } from './DataTable';
import { BetDetail, type OpenBet } from './BetDetail';
import { CURRENCY, Money, When } from './format';
import { FilterBar, useUrlFilters, type FilterField } from './Filters';
import { HE_A_ERRORS } from './labels';
import type { Command } from '../pages/Commands';

// cho: the ledger stake of a round still open (task ruby-cuoc-cham): the settled row replaces it, it has no detail
type Nguon = 'bet_history' | 'keno_ve' | 'lode_ve' | 'lodest_ve' | 'miracle' | 'saba' | 'bida' | 'cho';
export type Bet = {
  key: string; nguon: Nguon; ref: string; ban_ghi: number; user_id: number; user_name: string | null; game_id: string; game_name: string; sid: string;
  created_time: number; game_stake: number; payout: number; refund: number; net: number;
  // 'tron' = VIN and XU on one ticket: the money columns are the VIN part, the XU part is in the detail
  tien: 'vin' | 'xu' | 'xeng' | 'tron' | null;
  ket_qua: 'cho' | 'thang' | 'thua' | 'hoa' | 'hoan'; chi_tiet: string; game_ticket_status: string; game_your_bet: string | null;
  edited_payout?: number | null;
};
type BetPage = { items: Bet[]; nextBefore: string | null; games: { id: string; name: string }[] };

export const KET_QUA: Record<Bet['ket_qua'], [string, 'blue' | 'green' | 'red' | 'gray' | 'teal']> = {
  cho: ['Đang chờ', 'blue'], thang: ['Thắng', 'green'], thua: ['Thua', 'red'], hoa: ['Hòa', 'gray'], hoan: ['Hoàn', 'teal'],
};

// amounts in the ticket's currency: xu and xeng are play currencies, not won
export function Tien({ r, v, signed = false }: { r: Bet; v: number; signed?: boolean }) {
  if (r.tien === 'xu') return <Money v={v} signed={signed} unit="xu" />;
  if (r.tien === 'xeng') return <span><Money v={v} signed={signed} /> xèng</span>;
  return <Money v={v} signed={signed} />;
}

const columns: ColumnDef<Bet, any>[] = [
  { header: 'Lúc', accessorKey: 'created_time', cell: (c) => <When v={Number(c.getValue())} /> },
  { header: 'Người chơi', accessorKey: 'user_id', cell: (c) => (
    <Link className="cell-link" to={`/users/${c.getValue()}`} title={`Mã ${c.getValue()}`} onClick={(e) => e.stopPropagation()}>{c.row.original.user_name ?? <span className="num">{c.getValue()}</span>}</Link>
  ) },
  { header: 'Game', accessorKey: 'game_name', cell: (c) => <span className="bet-game" title={c.row.original.game_id}>{c.getValue()}</span> },
  { header: 'Phiên', accessorKey: 'sid', cell: (c) => <span className="num" title={`${c.row.original.nguon} #${c.row.original.ban_ghi}`}>{c.getValue()}</span>, enableSorting: false },
  { header: 'Cược', accessorKey: 'game_stake', cell: (c) => <Tien r={c.row.original} v={c.getValue()} /> },
  { header: 'Nhận về', accessorKey: 'payout', cell: (c) => {
    const r = c.row.original;
    if (r.ket_qua === 'cho') return <span className="muted">-</span>;
    return (
      <span>
        <Tien r={r} v={r.payout} />
        {r.refund > 0 ? <span className="muted"> (hoàn <Tien r={r} v={r.refund} />)</span> : null}
        {r.tien === 'tron' ? <span className="muted"> (phần vin)</span> : null}
      </span>
    );
  } },
  { header: 'Lời / lỗ', accessorKey: 'net', cell: (c) => (c.row.original.ket_qua === 'cho' ? <span className="muted">-</span> : <Tien r={c.row.original} v={c.getValue()} signed />) },
  { header: 'Kết quả', accessorKey: 'ket_qua', cell: (c) => {
    const [ten, mau] = KET_QUA[c.row.original.ket_qua] ?? ['?', 'gray'];
    return <Tag type={mau} size="sm">{ten}</Tag>;
  } },
  { header: 'Chi tiết cược', accessorKey: 'chi_tiet', enableSorting: false,
    cell: (c) => <span className="bet-detail" title={c.row.original.game_your_bet ?? ''}>{c.getValue()}</span> },
];

const NGUON = [
  ['bet_history', 'Lịch sử cược (mã ván)'],
  ['keno_ve', 'Keno (mã vé)'],
  ['lode_ve', 'Lô đề (mã vé)'],
  ['lodest_ve', 'Lô đề siêu tốc (mã vé)'],
  ['miracle', 'Miracle (mã ván vendor::transaction)'],
] as const;

function SuaKetQua({ ve, dong, onClose }: { ve: Bet | null; dong: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [thua, setThua] = useState(true);
  const [thang, setThang] = useState('');
  const [nguon, setNguon] = useState<(typeof NGUON)[number][0]>('bet_history');
  const [ref, setRef] = useState('');
  const [xong, setXong] = useState<Command | null>(null);
  const m = useMutation({
    mutationFn: () => api.post<{ command: Command }>('/api/admin/bets/sua-ket-qua', {
      nguon: ve ? ve.nguon : nguon,
      ref: ve ? ve.ref : ref.trim(),
      user_id: ve ? ve.user_id : undefined,
      moi: thua ? { thua: true } : { thang: Number(thang) },
    }),
    onSuccess: (r) => { setXong(r.command); qc.invalidateQueries({ queryKey: ['bets'] }); },
  });
  const mo = dong || !!ve;
  const soThang = Number(thang);
  const du = !thua && Number.isInteger(soThang) && soThang >= 0;
  return (
    <Modal open={mo} modalHeading={ve ? `Sửa ${ve.game_name} · phiên ${ve.sid}` : 'Sửa vé đã chốt'}
      primaryButtonText="Gửi sang hệ A" secondaryButtonText="Đóng"
      primaryButtonDisabled={(!thua && !du) || (!ve && !ref.trim()) || m.isPending}
      onRequestClose={() => { setXong(null); onClose(); }}
      onRequestSubmit={() => m.mutate()}>
      <div className="stack">
        <p>Ví người chơi đổi đúng phần chênh. Không đủ tiền để thu hồi thì trừ hết số dư và ghi phần còn lại là nợ.</p>
        {ve ? null : (
          <>
            <Select id="nguon" labelText="Nguồn" value={nguon} onChange={(e) => setNguon(e.target.value as typeof nguon)}>
              {NGUON.map(([id, ten]) => <SelectItem key={id} value={id} text={ten} />)}
            </Select>
            <TextInput id="ref" labelText="Mã vé / mã ván" value={ref} onChange={(e) => setRef(e.target.value)} />
          </>
        )}
        <RadioButtonGroup name="moi" legendText="Kết quả mới" valueSelected={thua ? 'thua' : 'thang'}
          onChange={(v) => setThua(v === 'thua')}>
          <RadioButton id="thua" value="thua" labelText="Thua (tiền thắng về 0)" />
          <RadioButton id="thang" value="thang" labelText="Thắng, số tiền mới" />
        </RadioButtonGroup>
        {thua ? null : <TextInput id="tien" labelText={`Tiền thắng mới (${CURRENCY.symbol})`} inputMode="numeric" value={thang} onChange={(e) => setThang(e.target.value.replace(/\D/g, ''))} />}
        {m.error ? <InlineError error={m.error} /> : null}
        {xong ? <p>{xong.status === 'SUCCEEDED' ? 'Đã sửa.' : (HE_A_ERRORS[xong.lastError ?? ''] ?? xong.lastError ?? xong.status)} <Link className="cell-link" to={'/commands/' + xong.id}>Xem lệnh</Link></p> : null}
      </div>
    </Modal>
  );
}

// 2 s for the views the server reads from a recent window or a pushed-down filter (none, player, a non-Miracle game);
// any other filter can need the whole feed (seconds on prod) and keeps the old 15 s
export const BETS_POLL_MS = 2_000;
export const BETS_POLL_SLOW_MS = 15_000;
export const betsPollMs = (q: Record<string, unknown>) =>
  Object.entries(q).every(([k, v]) => v === undefined || v === '' || k === 'userId' || k === 'user' || (k === 'game' && !String(v).startsWith('miracle:')))
    ? BETS_POLL_MS : BETS_POLL_SLOW_MS;

const KEYS = ['user', 'game', 'status', 'round', 'stakeMin', 'stakeMax', 'from', 'to'] as const;
const STATUS = [['', 'Tất cả'], ['running', 'Đang chạy'], ['done', 'Đã xong']] as const;
// the URL keeps `user`; the API filters by userId (a player's tab fixes it)
const query = (q: Record<string, string>, userId?: number) => {
  const { user, ...rest } = q;
  return { ...rest, userId: userId ?? (user ? Number(user) : undefined) };
};

export function BetsTable({ userId }: { userId?: number }) {
  const { can } = useAuth();
  const [ve, setVe] = useState<Bet | null>(null);
  const [mo, setMo] = useState<OpenBet | null>(null);
  const [dong, setDong] = useState(false);
  const f = useUrlFilters(KEYS, { prefix: userId ? 'bt_' : '' });
  const q = useInfiniteQuery({
    queryKey: ['bets', userId ?? null, f.query],
    queryFn: ({ pageParam }) => api.get<BetPage>('/api/admin/bets', { ...query(f.query, userId), before: pageParam, limit: 50 }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (p) => p.nextBefore ?? undefined,
    // a bet shows here about as fast as its Telegram notice (task ruby-cuoc-cham): the stake of an open round is read
    // from the ledger and the page reads its newest rows first (~50 ms on prod), so the list refreshes every 2 s
    refetchInterval: betsPollMs(query(f.query, userId)),
  });
  const rows = q.data?.pages.flatMap((p) => p.items);
  const games = q.data?.pages[0]?.games ?? [];
  const fields: FilterField[] = [
    ...(userId ? [] : [{ key: 'user', label: 'Mã người chơi', kind: 'number' } as const]),
    { key: 'game', label: 'Game', kind: 'select', options: [['', 'Tất cả'], ...games.map((g) => [g.id, g.name] as const)] },
    { key: 'status', label: 'Trạng thái', kind: 'select', options: STATUS },
    { key: 'round', label: 'Phiên', kind: 'text' },
    { key: 'stakeMin', label: `Cược từ (${CURRENCY.symbol})`, kind: 'number' },
    { key: 'stakeMax', label: `Cược đến (${CURRENCY.symbol})`, kind: 'number' },
    { key: 'from', label: 'Từ ngày', kind: 'date' },
    { key: 'to', label: 'Đến ngày', kind: 'date' },
  ];
  const filtered = f.active;

  return (
    <>
      <FilterBar id={userId ? 'btu' : 'bt'} fields={fields} filters={f} exportPath="/api/admin/bets" exportQuery={query(f.query, userId)}>
        {can('bet:result:edit') ? <Button kind="ghost" size="md" onClick={() => setDong(true)}>Sửa vé khác</Button> : null}
      </FilterBar>
      <DataTable
        columns={can('bet:result:edit') ? columns.concat([{
          header: '', id: 'sua', enableSorting: false,
          // SABA / Bi-a: no result edit command source
          cell: (c) => c.row.original.game_ticket_status === 'Running' || c.row.original.nguon === 'saba' || c.row.original.nguon === 'bida' ? null : (
            <Button kind="ghost" size="sm" onClick={(e: { stopPropagation: () => void }) => { e.stopPropagation(); setVe(c.row.original); }}>Sửa</Button>
          ),
        }]) : columns}
        data={rows}
        loading={q.isLoading}
        error={q.error}
        onRetry={() => q.refetch()}
        rowKey={(r) => r.key}
        onRowClick={(r) => { if (r.nguon !== 'cho') setMo({ nguon: r.nguon, ref: r.ref, user_id: r.user_id }); }}
        empty={{ title: filtered ? 'Không có ván nào khớp bộ lọc' : 'Chưa có lịch sử cược', body: filtered ? 'Nới bộ lọc để xem thêm.' : 'Mọi ván và vé cược (game, Lô đề, Keno, Live Casino) được ghi ở đây.' }}
        footer={<>
          <span className="muted">{rows?.length ?? 0} vé / ván đã tải</span>
          {q.hasNextPage ? <Button kind="ghost" size="sm" onClick={() => q.fetchNextPage()} disabled={q.isFetchingNextPage}>Tải thêm</Button> : null}
        </>}
      />
      <BetDetail bet={mo} onClose={() => setMo(null)} />
      {can('bet:result:edit') ? <SuaKetQua ve={ve} dong={dong} onClose={() => { setVe(null); setDong(false); }} /> : null}
    </>
  );
}
